import { useState, useEffect, useMemo, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Eye, Logo } from '../components/Icons.jsx';

const passages = [
  {
    title: 'The placebo effect',
    text: `In medicine, the placebo effect describes a measurable improvement in a patient's condition that cannot be explained by the active ingredient of a treatment. The effect is most pronounced for symptoms that depend on subjective reporting, such as pain or fatigue, but it has also been observed in conditions with objective markers. Researchers now understand that expectation, conditioning and the broader clinical encounter all contribute. The implication for trial design is significant: any treatment must be shown to outperform a credible placebo before clinical claims can be supported. Some bioethicists argue that, where no proven treatment exists, the controlled use of placebo can be both effective and ethical.`
  },
  {
    title: 'NHS waiting times',
    text: `Waiting times in the NHS have been the focus of policy debate for decades. Although total funding has increased in real terms, the demand created by an ageing population and the long tail of pandemic-deferred care has outpaced capacity gains. Workforce shortages, particularly among trained nurses and general practitioners, are the most cited cause by trust-level leaders. Reforms targeting elective backlogs have included independent-sector partnerships and centralised triage hubs, but the impact on emergency department waits has been smaller. Patient advocacy groups argue that any sustainable improvement requires not only more clinicians but also primary care reform that prevents avoidable acute presentations.`
  },
  {
    title: 'Antibiotic resistance',
    text: `Antibiotic resistance is one of the most pressing global health threats of the twenty-first century. Bacteria evolve rapidly under selection pressure, and the overuse of broad-spectrum antibiotics in both human medicine and agriculture has accelerated the emergence of resistant strains. Once-routine infections — urinary tract, post-surgical, respiratory — are returning to the threshold of untreatable. Antimicrobial stewardship programmes encourage clinicians to narrow prescriptions, shorten courses and confirm bacterial cause where possible. Public-facing campaigns, by contrast, focus on patient expectation: a sore throat does not need an antibiotic, but the doctor needs the patient to accept that.`
  }
];

const sizes = [2, 3, 4];
const paces = [
  { label: 'Slow', ms: 600, wpmTarget: '~200 WPM' },
  { label: 'Medium', ms: 400, wpmTarget: '~300 WPM' },
  { label: 'Fast', ms: 280, wpmTarget: '~430 WPM' },
  { label: 'Sprint', ms: 200, wpmTarget: '~600 WPM' }
];

export default function DrillChunk() {
  const nav = useNavigate();
  const [stage, setStage] = useState('brief');
  const [chunkSize, setChunkSize] = useState(3);
  const [paceIdx, setPaceIdx] = useState(1);
  const [passageIdx, setPassageIdx] = useState(0);
  const [chunkIdx, setChunkIdx] = useState(0);
  const [countdown, setCountdown] = useState(3);
  const startRef = useRef(0);
  const [elapsed, setElapsed] = useState(0);

  const pace = paces[paceIdx];
  const passage = passages[passageIdx];

  const chunks = useMemo(() => {
    const words = passage.text.split(/\s+/).filter(Boolean);
    const out = [];
    for (let i = 0; i < words.length; i += chunkSize) out.push(words.slice(i, i + chunkSize).join(' '));
    return out;
  }, [passage.text, chunkSize]);

  useEffect(() => {
    if (stage !== 'countdown') return;
    if (countdown <= 0) {
      startRef.current = Date.now();
      setStage('running');
      return;
    }
    const t = setTimeout(() => setCountdown((c) => c - 1), 800);
    return () => clearTimeout(t);
  }, [stage, countdown]);

  useEffect(() => {
    if (stage !== 'running') return;
    if (chunkIdx >= chunks.length) {
      setElapsed(Date.now() - startRef.current);
      setStage('done');
      return;
    }
    const t = setTimeout(() => setChunkIdx((c) => c + 1), pace.ms);
    return () => clearTimeout(t);
  }, [stage, chunkIdx, chunks.length, pace.ms]);

  const wordCount = passage.text.split(/\s+/).filter(Boolean).length;
  const wpm = elapsed ? Math.round((wordCount / elapsed) * 60000) : 0;

  function startDrill() {
    setChunkIdx(0);
    setCountdown(3);
    setElapsed(0);
    setStage('countdown');
  }

  function exit() {
    if (stage === 'running' && !confirm('Exit this drill? Your progress will be lost.')) return;
    nav('/reading-lab');
  }

  return (
    <div style={{ background: 'var(--bg)', minHeight: '100vh' }}>
      <header style={{ background: '#fff', borderBottom: '1px solid var(--border)', padding: '14px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <button onClick={exit} className="back-link" style={{ marginBottom: 0 }}>
          <ArrowLeft width="16" height="16" />Exit drill
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Logo width="28" height="28" onLight />
          <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1, fontFamily: 'Montserrat, sans-serif' }}>
            <span style={{ fontSize: 16, fontWeight: 800, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--brand-deep)' }}>Medi</span>
            <span style={{ fontSize: 8.5, fontWeight: 600, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--text-muted)', marginTop: 3 }}>powered by Medic Mind</span>
          </div>
        </div>
        <div style={{ minWidth: 80, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: 'var(--text-muted)', fontSize: 14 }}>
          {stage === 'running' && `${chunkIdx} / ${chunks.length}`}
        </div>
      </header>

      <main className="main drill-shell">
        {stage === 'brief' && (
          <>
            <div className="page-head">
              <div className="kicker" style={{ color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '0.12em', fontWeight: 700, fontSize: 12 }}><Eye width="14" height="14" style={{ verticalAlign: 'middle', marginRight: 6 }} />Speed Reader · Drill 01</div>
              <h1>Learn how to chunk</h1>
              <p className="subtitle">Most readers fix on one word at a time. Top UCAT VR scorers fix on 2–4. We're going to force the habit. Read each chunk as it appears — don't go back, don't speak the words in your head, just keep your eyes moving forward.</p>
            </div>

            <div className="card" style={{ marginBottom: 18 }}>
              <h3 style={{ margin: '0 0 14px', fontSize: 18 }}>Settings</h3>

              <Setting label="Words per chunk" hint="Bigger chunks force a wider eye span.">
                <ChipRow options={sizes.map(String)} value={String(chunkSize)} onChange={(v) => setChunkSize(Number(v))} />
              </Setting>

              <Setting label="Pace" hint={`${pace.wpmTarget} at ${chunkSize} words / chunk.`}>
                <ChipRow options={paces.map((p) => p.label)} value={pace.label} onChange={(v) => setPaceIdx(paces.findIndex((p) => p.label === v))} />
              </Setting>

              <Setting label="Passage" hint={`${wordCount} words.`}>
                <ChipRow options={passages.map((p) => p.title)} value={passage.title} onChange={(v) => setPassageIdx(passages.findIndex((p) => p.title === v))} />
              </Setting>
            </div>

            <button onClick={startDrill} className="btn btn-primary" style={{ fontSize: 16, padding: '14px 24px' }}>Start drill <ArrowRight width="18" height="18" /></button>
          </>
        )}

        {stage === 'countdown' && (
          <div className="drill-stage" style={{ marginTop: 60 }}>
            <div style={{ fontSize: 96, fontWeight: 700, color: 'var(--brand-purple)', letterSpacing: '-0.02em' }}>{countdown || 'Go!'}</div>
            <p style={{ color: 'var(--text-muted)', marginTop: 14 }}>Eyes forward. Don't go back.</p>
          </div>
        )}

        {stage === 'running' && (
          <>
            <div className="drill-stage" style={{ marginTop: 40 }}>
              <div className="chunk-display">
                <span className="chunk">{chunks[chunkIdx] || ''}</span>
              </div>
              <div className="drill-progress">
                <div className="progress"><div className="progress-bar" style={{ width: `${(chunkIdx / chunks.length) * 100}%` }} /></div>
                <div className="meta-row">
                  <span>Chunk {chunkIdx + 1} of {chunks.length}</span>
                  <span>{pace.label} pace · {chunkSize} words</span>
                </div>
              </div>
            </div>
            <div style={{ textAlign: 'center', marginTop: 18 }}>
              <button onClick={() => { setElapsed(Date.now() - startRef.current); setStage('done'); }} className="btn btn-secondary">I lost it — end early</button>
            </div>
          </>
        )}

        {stage === 'done' && (
          <>
            <div className="page-head">
              <div className="kicker" style={{ color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '0.12em', fontWeight: 700, fontSize: 12 }}>Drill complete</div>
              <h1>Nice work.</h1>
              <p className="subtitle">You read "{passage.title}" at {pace.label.toLowerCase()} pace. Here's how it landed.</p>
            </div>

            <div className="drill-summary">
              <SumStat v={wpm} l="Words per minute" />
              <SumStat v={`${chunkSize}`} l="Words per chunk" />
              <SumStat v={`${(elapsed / 1000).toFixed(1)}s`} l="Total time" />
            </div>

            <div className="card" style={{ marginBottom: 20 }}>
              <h3 style={{ margin: '0 0 8px', fontSize: 18 }}>What's next</h3>
              <p style={{ color: 'var(--text-2)', margin: 0 }}>
                Real UCAT VR passages are 200–400 words and you get ~28s per question. If you're under 300 WPM, push the chunk size up. If you're over 400 WPM but it felt mechanical, you're skimming — go down a pace and let comprehension catch up.
              </p>
            </div>

            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <button onClick={startDrill} className="btn btn-primary">Run another <ArrowRight width="16" height="16" /></button>
              <Link to="/reading-lab" className="btn btn-secondary">Back to Reading Lab</Link>
              <Link to="/medi" className="btn btn-secondary">Ask Ash about pacing</Link>
            </div>
          </>
        )}
      </main>
    </div>
  );
}

function Setting({ label, hint, children }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
        <strong style={{ fontSize: 14 }}>{label}</strong>
        <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>{hint}</span>
      </div>
      {children}
    </div>
  );
}

function ChipRow({ options, value, onChange }) {
  return (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
      {options.map((opt) => (
        <button
          key={opt}
          onClick={() => onChange(opt)}
          style={{
            padding: '8px 14px',
            borderRadius: 999,
            border: `1px solid ${opt === value ? 'var(--brand-purple)' : 'var(--border)'}`,
            background: opt === value ? 'var(--accent-soft)' : '#fff',
            color: opt === value ? 'var(--brand-deep)' : 'var(--text-2)',
            fontWeight: opt === value ? 600 : 500,
            fontSize: 14
          }}
        >{opt}</button>
      ))}
    </div>
  );
}

function SumStat({ v, l }) {
  return (
    <div className="stat">
      <div className="v">{v}</div>
      <div className="l">{l}</div>
    </div>
  );
}
